import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { FC, useContext, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { MEDIA_BASE_URL } from "../../api/constants";
import { TitleContext } from "../../contexts/TitleContext";
import useAuth from "../../hooks/useAuth";
import { error, primaryColor, primary, secondary, surface } from "../../theme/themeColors";

interface DashboardNavbarProps {
  toggleMobileSideBar: () => void;
}

const DashboardNavbar: FC<DashboardNavbarProps> = ({ toggleMobileSideBar }) => {
  const { title } = useContext(TitleContext);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Pages set their own title via useTitle; fall back to the URL segment so the
  // bar is never blank on a page that forgot to.
  const pageTitle =
    title ||
    location.pathname.split("/").filter(Boolean).slice(-1)[0]?.replace(/-/g, " ") ||
    "Dashboard";

  const photo = user?.profile?.photo_url;
  const initial = (user?.first_name?.[0] || user?.email?.[0] || "U").toUpperCase();

  const handleSignOut = () => {
    setAnchorEl(null);
    logout?.();
    toast.success("Signed out successfully");
    navigate("/login");
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: surface.paper,
        borderBottom: `1px solid ${surface.border}`,
        height: 60,
        justifyContent: "center",
      }}
    >
      <Toolbar sx={{ px: { xs: 2, md: 3 }, gap: 2, minHeight: "60px !important" }}>
        <IconButton
          onClick={toggleMobileSideBar}
          sx={{ display: { md: "none" }, color: secondary[500] }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          noWrap
          variant="h6"
          sx={{
            color: secondary[500],
            fontWeight: 700,
            fontSize: 16,
            textTransform: "capitalize",
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          {pageTitle}
        </Typography>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Account">
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
            <Avatar
              src={photo ? `${MEDIA_BASE_URL}${photo}` : undefined}
              sx={{
                width: 34,
                height: 34,
                fontSize: 13,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${primaryColor}, ${primary[500]})`,
              }}
            >
              {initial}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{ elevation: 3, sx: { mt: 1, minWidth: 220 } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: secondary[500] }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography sx={{ fontSize: 11, color: secondary[400] }}>
              {user?.email || user?.phone_number}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              navigate("/profile");
            }}
            sx={{ py: 1.5, fontSize: 13 }}
          >
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" />
            </ListItemIcon>
            My Profile
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={handleSignOut}
            sx={{ py: 1.5, fontSize: 13, color: error.main }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: error.main }} />
            </ListItemIcon>
            Sign Out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default DashboardNavbar;
